import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { formatDateTime } from "@/lib/utils";
import { Calculator, Scale } from "lucide-react";

// Barème des commissions freelances, VERSIONNÉ : chaque enregistrement crée
// une nouvelle version (auditée) ; les écritures gardent la version (et donc
// le %) qui les a chiffrées. Modèle v2 (décision fondateur 05/10/2026) :
// SDR et Sales touchent chacun un % de l'encaissé HT des clients amenés /
// signés pendant N mois, versé dans leur cagnotte à chaque paiement ; prime
// SDR par RDV facultative (0 par défaut) ; garantie et minimum de versement
// réglables. Le % est modifiable à tout moment : seules les écritures FUTURES
// changent.
// Endpoints : admin.commissions.getPlan / updatePlan / planExample (SUPER_ADMIN).

type SalesMode = "NONE" | "PERCENT_COLLECTED" | "FIXED_PER_CONVERSION";

const MODE_LABELS: Record<SalesMode, string> = {
  PERCENT_COLLECTED: "% de l'encaissé HT, pendant N mois (cagnotte)",
  FIXED_PER_CONVERSION: "Prime fixe à la première facture payée",
  NONE: "Aucune commission Sales (conversions tracées seulement)",
};

const eur = (cents: number | null | undefined) =>
  cents == null ? "-" : (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

/** « 49,50 » → 4950 ; vide → null. */
function toCents(v: string): number | null {
  if (!v.trim()) return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}
const fromCents = (c: number | null | undefined) => (c == null ? "" : String(c / 100).replace(".", ","));
const toBps = (v: string): number | null => (v.trim() ? Math.round(Number(v.replace(",", ".")) * 100) : null);
const fromBps = (b: number | null | undefined) => (b == null ? "" : String(b / 100).replace(".", ","));

export default function CommissionPlan() {
  const utils = trpc.useUtils();
  const plan = trpc.admin.commissions.getPlan.useQuery();
  const active = plan.data?.active;

  const [sdrPercent, setSdrPercent] = useState("");
  const [sdrDuration, setSdrDuration] = useState("");
  const [sdrAmount, setSdrAmount] = useState("");
  const [mode, setMode] = useState<SalesMode>("PERCENT_COLLECTED");
  const [percent, setPercent] = useState("");
  const [duration, setDuration] = useState("");
  const [fixed, setFixed] = useState("");
  const [holdDays, setHoldDays] = useState("0");
  const [payoutMin, setPayoutMin] = useState("50");
  const [windowDays, setWindowDays] = useState("90");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!active) return;
    setSdrPercent(fromBps(active.sdrPercentBps));
    setSdrDuration(active.sdrDurationMonths == null ? "" : String(active.sdrDurationMonths));
    setSdrAmount(fromCents(active.sdrAmountPerHonoredMeetingCents));
    setMode(active.salesMode as SalesMode);
    setPercent(fromBps(active.salesPercentBps));
    setDuration(active.salesDurationMonths == null ? "" : String(active.salesDurationMonths));
    setFixed(fromCents(active.salesFixedCents));
    setHoldDays(String(active.holdDays ?? 0));
    setPayoutMin(fromCents(active.payoutMinCents ?? 5000));
    setWindowDays(String(active.attributionWindowDays));
  }, [active]);

  const update = trpc.admin.commissions.updatePlan.useMutation({
    onSuccess: (r) => {
      toast.success(`Barème v${r.version} enregistré` + (r.repriced ? ` (${r.repriced} écriture(s) SDR revalorisée(s)).` : "."));
      setNote("");
      void utils.admin.commissions.getPlan.invalidate();
      void utils.admin.commissions.planExample.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  // Calculateur vivant : exemple au barème EN VIGUEUR (côté serveur) et
  // simulation du formulaire en cours de saisie (côté client).
  const [exMonthly, setExMonthly] = useState("200");
  const [exClients, setExClients] = useState("100");
  const exMonthlyCents = toCents(exMonthly) ?? 20_000;
  const exClientsN = Math.max(1, Math.min(10_000, Number(exClients) || 1));
  const example = trpc.admin.commissions.planExample.useQuery({ monthlyCents: exMonthlyCents, clients: exClientsN }, { keepPreviousData: true });

  const sdrBps = toBps(sdrPercent);
  const salesBps = toBps(percent);
  const draft = (bps: number | null, months: string) => {
    const m = months.trim() ? Number(months) : 12;
    const perMonth = Math.round((exMonthlyCents * (bps ?? 0)) / 10_000);
    return { perMonth, perClient: perMonth * m, total: perMonth * m * exClientsN, months: m };
  };
  const draftSdr = draft(sdrBps, sdrDuration);
  const draftSales = draft(mode === "PERCENT_COLLECTED" ? salesBps : null, duration);

  const invalid =
    (sdrPercent.trim() !== "" && (sdrBps == null || sdrBps < 0 || sdrBps > 10_000)) ||
    (sdrAmount.trim() !== "" && toCents(sdrAmount) === null) ||
    (mode === "PERCENT_COLLECTED" && (!salesBps || salesBps <= 0 || salesBps > 10_000)) ||
    (mode === "FIXED_PER_CONVERSION" && !toCents(fixed)) ||
    !(Number(holdDays) >= 0 && Number(holdDays) <= 365) ||
    toCents(payoutMin) === null ||
    !(Number(windowDays) >= 1 && Number(windowDays) <= 730);

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Scale className="h-6 w-6" /> Barème des commissions
          </h1>
          <p className="text-sm text-muted-foreground">
            Freelances de prospection. Chaque modification crée une nouvelle version, tracée dans l'Audit Trail ; les commissions déjà inscrites gardent leur % d'origine, seules les suivantes prennent le nouveau.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Version en vigueur : v{active?.version ?? 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                className="space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (invalid) return;
                  update.mutate({
                    sdrAmountPerHonoredMeetingCents: toCents(sdrAmount) ?? 0,
                    sdrPercentBps: sdrBps,
                    sdrDurationMonths: sdrBps && sdrDuration.trim() ? Number(sdrDuration) : null,
                    salesMode: mode,
                    salesPercentBps: mode === "PERCENT_COLLECTED" ? salesBps : null,
                    salesDurationMonths: mode === "PERCENT_COLLECTED" && duration.trim() ? Number(duration) : null,
                    salesFixedCents: mode === "FIXED_PER_CONVERSION" ? toCents(fixed) : null,
                    holdDays: Number(holdDays),
                    payoutMinCents: toCents(payoutMin) ?? 5000,
                    attributionWindowDays: Number(windowDays),
                    note: note.trim() || undefined,
                  });
                }}
              >
                <div className="rounded-md border p-3 space-y-3">
                  <p className="text-sm font-medium">SDR (prise de RDV)</p>
                  <div className="grid grid-cols-2 gap-3 max-w-md">
                    <div className="space-y-1.5">
                      <Label htmlFor="cp-sdr-pct">Part de l'encaissé HT (%)</Label>
                      <Input id="cp-sdr-pct" value={sdrPercent} onChange={(e) => setSdrPercent(e.target.value)} placeholder="ex. 10" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="cp-sdr-dur">Durée (mois)</Label>
                      <Input id="cp-sdr-dur" value={sdrDuration} onChange={(e) => setSdrDuration(e.target.value)} placeholder="12" />
                    </div>
                  </div>
                  <div className="space-y-1.5 max-w-xs">
                    <Label htmlFor="cp-sdr">Prime par RDV tenu et validé (€, facultatif)</Label>
                    <Input id="cp-sdr" value={sdrAmount} onChange={(e) => setSdrAmount(e.target.value)} placeholder="0" />
                    <p className="text-xs text-muted-foreground">0 ou vide = pas de prime au RDV : le SDR est payé sur l'encaissé, comme le Sales.</p>
                  </div>
                </div>

                <div className="rounded-md border p-3 space-y-3">
                  <p className="text-sm font-medium">Sales (démo et signature)</p>
                  <div className="space-y-1.5">
                    <Label htmlFor="cp-mode">Mode de commission</Label>
                    <select id="cp-mode" value={mode} onChange={(e) => setMode(e.target.value as SalesMode)} className="w-full max-w-md h-9 rounded-md border border-input bg-background px-3 py-1 text-sm">
                      {(Object.keys(MODE_LABELS) as SalesMode[]).map((k) => (
                        <option key={k} value={k}>{MODE_LABELS[k]}</option>
                      ))}
                    </select>
                  </div>
                  {mode === "PERCENT_COLLECTED" && (
                    <div className="grid grid-cols-2 gap-3 max-w-md">
                      <div className="space-y-1.5">
                        <Label htmlFor="cp-pct">Part de l'encaissé HT (%)</Label>
                        <Input id="cp-pct" value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="ex. 10" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="cp-dur">Durée (mois)</Label>
                        <Input id="cp-dur" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="12" />
                      </div>
                    </div>
                  )}
                  {mode === "FIXED_PER_CONVERSION" && (
                    <div className="space-y-1.5 max-w-xs">
                      <Label htmlFor="cp-fix">Prime par conversion (€)</Label>
                      <Input id="cp-fix" value={fixed} onChange={(e) => setFixed(e.target.value)} />
                    </div>
                  )}
                </div>

                <div className="rounded-md border p-3 space-y-3">
                  <p className="text-sm font-medium">Cagnotte et versements</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="cp-hold">Garantie avant disponibilité (jours)</Label>
                      <Input id="cp-hold" value={holdDays} onChange={(e) => setHoldDays(e.target.value)} />
                      <p className="text-xs text-muted-foreground">0 = disponible dès le paiement du client.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="cp-min">Minimum par versement (€)</Label>
                      <Input id="cp-min" value={payoutMin} onChange={(e) => setPayoutMin(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="cp-win">Fenêtre d'attribution (jours)</Label>
                      <Input id="cp-win" value={windowDays} onChange={(e) => setWindowDays(e.target.value)} />
                      <p className="text-xs text-muted-foreground">Inscription du client après le RDV.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="cp-note">Note interne (facultatif)</Label>
                  <Input id="cp-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex. grille validée le 15/10" />
                </div>

                <Button type="submit" disabled={invalid || update.isLoading}>
                  {update.isLoading ? "Enregistrement…" : "Enregistrer une nouvelle version"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Calculator className="h-4 w-4" /> Exemple vivant</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Abonnement (€ HT / mois)</Label>
                  <Input value={exMonthly} onChange={(e) => setExMonthly(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Clients</Label>
                  <Input value={exClients} onChange={(e) => setExClients(e.target.value)} />
                </div>
              </div>
              <div className="rounded-md bg-muted p-3 space-y-1">
                <p className="text-xs text-muted-foreground">Barème en vigueur (v{active?.version ?? 0})</p>
                {example.data ? (
                  <>
                    <p>SDR : <strong>{eur(example.data.sdr.monthlyPerClientCents)}</strong> / mois / client, {eur(example.data.sdr.perClientCents)} sur {example.data.sdr.months} mois, <strong>{eur(example.data.sdr.totalCents)}</strong> pour {exClientsN} clients</p>
                    <p>Sales : <strong>{eur(example.data.sales.monthlyPerClientCents)}</strong> / mois / client, {eur(example.data.sales.perClientCents)} sur {example.data.sales.months} mois, <strong>{eur(example.data.sales.totalCents)}</strong> pour {exClientsN} clients</p>
                  </>
                ) : (
                  <p className="text-muted-foreground">Calcul…</p>
                )}
              </div>
              <div className="rounded-md border p-3 space-y-1">
                <p className="text-xs text-muted-foreground">Formulaire en cours (avant enregistrement)</p>
                <p>SDR : <strong>{eur(draftSdr.perMonth)}</strong> / mois / client, {eur(draftSdr.perClient)} sur {draftSdr.months} mois, <strong>{eur(draftSdr.total)}</strong> au total</p>
                <p>Sales : <strong>{eur(draftSales.perMonth)}</strong> / mois / client, {eur(draftSales.perClient)} sur {draftSales.months} mois, <strong>{eur(draftSales.total)}</strong> au total</p>
                <p className="text-xs text-muted-foreground">Ex. 200 € / mois à 10 % + 10 % : 20 € par mois dans chaque cagnotte, 240 € par an et par rôle, 20 % du MRR annuel partagé.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Historique</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Depuis</TableHead>
                  <TableHead>SDR</TableHead>
                  <TableHead>Sales</TableHead>
                  <TableHead>Garantie</TableHead>
                  <TableHead>Min. versement</TableHead>
                  <TableHead>Fenêtre</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(plan.data?.history ?? []).map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>v{h.version}</TableCell>
                    <TableCell>{formatDateTime(h.effectiveFrom)}</TableCell>
                    <TableCell className="text-sm">
                      {h.sdrPercentBps ? `${h.sdrPercentBps / 100} % ${h.sdrDurationMonths ? `· ${h.sdrDurationMonths} mois` : "· sans limite"}` : "0 %"}
                      {h.sdrAmountPerHonoredMeetingCents ? ` + ${eur(h.sdrAmountPerHonoredMeetingCents)} / RDV` : ""}
                    </TableCell>
                    <TableCell className="text-sm">
                      {h.salesMode === "PERCENT_COLLECTED"
                        ? `${(h.salesPercentBps ?? 0) / 100} % ${h.salesDurationMonths ? `· ${h.salesDurationMonths} mois` : "· sans limite"}`
                        : h.salesMode === "FIXED_PER_CONVERSION"
                          ? `${eur(h.salesFixedCents)} / conversion`
                          : "Aucune"}
                    </TableCell>
                    <TableCell>{h.holdDays ?? 0} j</TableCell>
                    <TableCell>{eur(h.payoutMinCents ?? 5000)}</TableCell>
                    <TableCell>{h.attributionWindowDays} j</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{h.note ?? ""}</TableCell>
                  </TableRow>
                ))}
                {plan.data && plan.data.history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-sm text-muted-foreground">
                      Aucun barème enregistré : aucune commission n'est calculée tant que rien n'est saisi ici.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
